import 'src/global.css';

import { SWRConfig } from 'swr';
import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Outlet, RouterProvider } from 'react-router';

import { usePathname } from 'src/routes/hooks';

import { themeConfig, ThemeProvider } from 'src/theme';

import { ProgressBar } from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
import { SettingsDrawer, defaultSettings, SettingsProvider } from 'src/components/settings';

import { getEnv } from './config/envs';
import { LocalizationProvider } from './locales';
import { Snackbar } from './components/snackbar';
import { routesSection } from './routes/sections';
import { sentryCreateBrowserRouter } from './sentry';
import { AuthProvider } from './auth/context/firebase';

const key = `swr-app-cache-matto-${getEnv()}`;

function localStorageProvider() {
  // When initializing, we restore the data from `localStorage` into a map.
  const map = new Map(JSON.parse(localStorage.getItem(key) || '[]'));

  // Before unloading the app, we write back all the data into `localStorage`.
  window.addEventListener('beforeunload', () => {
    const appCache = JSON.stringify(Array.from(map.entries()));
    localStorage.setItem(key, appCache);
  });

  // We still use the map for write & read for performance.
  return map;
}

export function LocalProviders({ children }: { children: React.ReactNode }) {
  return (
    // @ts-expect-error shit swr
    <SWRConfig value={{ provider: localStorageProvider }}>
      <SettingsProvider defaultSettings={defaultSettings}>
        <LocalizationProvider>
          <ThemeProvider
            noSsr
            defaultMode={themeConfig.defaultMode}
            modeStorageKey={themeConfig.modeStorageKey}
          >
            <MotionLazy>
              <ProgressBar />
              <Snackbar />
              <SettingsDrawer defaultSettings={defaultSettings} />
              {children}
            </MotionLazy>
          </ThemeProvider>
        </LocalizationProvider>
      </SettingsProvider>
    </SWRConfig>
  );
}

// ----------------------------------------------------------------------

type AppProps = {
  children: React.ReactNode;
};

function App({ children }: AppProps) {
  useScrollToTop();

  return (
    <AuthProvider>
      <LocalProviders>{children}</LocalProviders>
    </AuthProvider>
  );
}

const router = sentryCreateBrowserRouter([
  {
    Component: () => (
      <App>
        <Outlet />
      </App>
    ),
    children: routesSection,
  },
]);

export default function AppWithRouter() {
  return (
    <HelmetProvider>
      <RouterProvider router={router} />
    </HelmetProvider>
  );
}

// ----------------------------------------------------------------------

function useScrollToTop() {
  const pathname = usePathname();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
